import React, { useEffect, useState, memo } from "react";
import Modal from "@/components/Modal/Modal";
import { TextField, Box } from "@mui/material";
import { Select } from "@/components/FormControl/Select/Select";
import type { RootState } from "@/store/rootReducer";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/store/hooks";
import { getWorkStation } from "@/store/slices/userSlice";

interface PunchInReportModalProps {
  modalOpen: boolean;
  closeModal: () => void;
  onSubmit: (report: string, workstation: string) => void;
  width?: string;
}

const PunchInReportModal: React.FC<PunchInReportModalProps> = ({
  modalOpen,
  closeModal,
  onSubmit,
  width,
}) => {
  const [report, setReport] = useState("");
  const [error, setError] = useState("");
  const [selectedWorkstation, setSelectedWorkstation] = useState<string>("");

  const dispatch = useAppDispatch();

  const workstationItems = useSelector(
    (store: RootState) => store?.user?.workStationList?.[0]?.Items || []
  );

  const workstationOptions = React.useMemo(
    () =>
      workstationItems.map((s: { Name: string; Id: string }) => ({
        label: s.Name,
        value: s.Id,
      })),
    [workstationItems]
  );

  useEffect(() => {
    if (modalOpen) {
      dispatch(getWorkStation());
    }
  }, [modalOpen, dispatch]);

  const handleDone = () => {
    if (!selectedWorkstation) {
      setError("Please select a workstation");
      return;
    }

    onSubmit(report, selectedWorkstation);

    // Reset
    setReport("");
    setSelectedWorkstation("");
    setError("");
    closeModal();
  };

  const handleCancel = () => {
    setReport("");
    setSelectedWorkstation("");
    setError("");
    closeModal();
  };

  return (
    <Modal
      modalOpen={modalOpen}
      closeModal={handleCancel}
      heading="Punch In Report"
      saveButtonText="Done"
      width={width || "500px"}
      errorMessage={error}
      onSave={handleDone}
    >
      <Box display="flex" flexDirection="column" gap={2} mt={1}>
        {/* Report (Optional) */}
        <TextField
          label="Report (Optional)"
          multiline
          rows={4}
          variant="outlined"
          value={report}
          onChange={(e) => setReport(e.target.value)}
          fullWidth
        />

        <Select
          name="workstation"
          placeholder="Select Workstation *"
          value={selectedWorkstation}
          onChange={setSelectedWorkstation}
          options={workstationOptions}
        />
      </Box>
    </Modal>
  );
};

export default memo(PunchInReportModal);
